import { computed, inject, Injectable, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { SavedTreeItem, VariableType } from '@selfai-platform/pipeline-common';
import { AlertService } from '@selfai-platform/shared';
import { TreeNode } from 'primeng/api';
import { NewTreeItem, PENDING_NEW_ITEM_ID, PendingNewItem, TreeDataItem } from '../models';
import { WorkflowVariablesStore } from '../store';
import { isGroup } from '../utils';
import { VariablesEditingStateService } from './variables-editing-state.service';

const MAX_TREE_DEPTH = 50;

@Injectable()
export class VariablesTreeDataService {
  private readonly store = inject(WorkflowVariablesStore);
  private readonly alertService = inject(AlertService);
  private readonly translateService = inject(TranslateService);
  private readonly editingStateService = inject(VariablesEditingStateService);

  private readonly expandedIds = signal<{ [key: string]: boolean }>({});

  readonly treeData = computed(() => this.buildTreeData());
  readonly groupOptions = computed(() => this.buildGroupOptions());

  updateExpandedState(nodeId: string, expanded: boolean): void {
    this.expandedIds.update((ids) => ({
      ...ids,
      [nodeId]: expanded,
    }));
  }

  expandParentNode(parent: TreeNode<SavedTreeItem> | null): void {
    if (parent?.key) {
      this.expandedIds.update((keys) => ({
        ...keys,
        [parent.key]: true,
      }));
    }
  }

  findGroupNodeByKey(treeNodeKey: string): TreeNode<TreeDataItem> | null {
    if (!treeNodeKey) return null;

    return this.findInNodes(this.groupOptions(), treeNodeKey);
  }

  findNodeByKey(treeNodeKey: string): TreeNode<TreeDataItem> | null {
    if (!treeNodeKey) return null;

    return this.findInNodes(this.treeData(), treeNodeKey);
  }

  buildFullGroupPath(groupId: string): string[] {
    if (!groupId) return [];

    const allGroups = this.store.groups();
    const group = allGroups.find((g) => g.id === groupId);

    if (!group) return [];

    const path: string[] = [];
    let currentGroup = group;

    while (currentGroup) {
      path.unshift(currentGroup.key);
      currentGroup = currentGroup.parentGroupId ? allGroups.find((g) => g.id === currentGroup.parentGroupId) : null;
    }

    return path;
  }

  buildAbsoluteVariablePath(parentGroupId: string, variableName: string): string {
    const groupPath = parentGroupId ? this.buildFullGroupPath(parentGroupId) : [];
    const fullPath = [...groupPath, variableName].join('.');

    return `$\{${fullPath}}`;
  }

  getUIGroupPath(groupId: string): string {
    return this.buildFullGroupPath(groupId).join(' -> ');
  }

  createNewItemNode(
    type: VariableType,
    parentGroupId?: string,
    parentItem: TreeDataItem | null = null,
  ): TreeNode<TreeDataItem> {
    return {
      key: PENDING_NEW_ITEM_ID,
      data: {
        id: PENDING_NEW_ITEM_ID,
        key: '',
        isNewItem: true,
        type,
        parentGroupId,
      } as NewTreeItem,
      parent: parentItem,
      leaf: type === VariableType.VARIABLE,
      children: type === VariableType.GROUP ? [] : undefined,
    };
  }

  updateTreeNodeExpanded(tree: TreeNode<TreeDataItem>[], nodeId: string, expanded: boolean): TreeNode<TreeDataItem>[] {
    return tree.map((node) => {
      if (node.key === nodeId) {
        return { ...node, expanded };
      }

      if (node.children?.length) {
        const updatedChildren = this.updateTreeNodeExpanded(node.children, nodeId, expanded);
        if (updatedChildren !== node.children) {
          return { ...node, children: updatedChildren };
        }
      }

      return node;
    });
  }

  private buildTreeData(): TreeNode<TreeDataItem>[] {
    const topLevelItems = this.store.topLevelItems();
    const tree = topLevelItems
      .map((item) => this.buildTreeNode(item))
      .filter((node): node is TreeNode<TreeDataItem> => node !== null);

    const pendingItem = this.editingStateService.getPendingNewItem();
    if (pendingItem) {
      const parentGroupId = pendingItem.parentGroupId;

      if (!parentGroupId) {
        const newNode = this.createNewItemNode(pendingItem.type, parentGroupId);
        tree.push(newNode);
      } else {
        this.addPendingItemToParent(tree, pendingItem, parentGroupId);
      }
    }

    return tree;
  }

  private buildTreeNode(item: TreeDataItem, depth = 0): TreeNode<TreeDataItem> | null {
    if (!item?.id) {
      console.error(`Item missing ID: ${JSON.stringify(item)}`);
      this.alertService.error(this.translateService.instant('workflow.variables.tree.error.missing.id'));

      return null;
    }

    if (depth > MAX_TREE_DEPTH) {
      console.error(`Maximum tree depth exceeded for item: ${JSON.stringify(item)}`);
      this.alertService.error(this.translateService.instant('workflow.variables.tree.error.tree.structure.too.deep'));

      return null;
    }

    if (isGroup(item)) {
      const groupItems = this.store.getChildren(item.id);
      const children: TreeNode<TreeDataItem>[] = groupItems
        .map((childItem) => this.buildTreeNode(childItem, depth + 1))
        .filter((node): node is TreeNode<TreeDataItem> => node !== null);

      return {
        key: item.id,
        data: item,
        children,
        expanded: this.expandedIds()[item.id] || false,
      };
    } else {
      return {
        key: item.id,
        data: item,
        leaf: true,
      };
    }
  }

  private buildGroupOptions(): TreeNode<TreeDataItem>[] {
    const allGroups = this.store.groups();
    const topLevelGroups = allGroups.filter((group) => !group.parentGroupId);

    return this.buildGroupTree(topLevelGroups);
  }

  private buildGroupTree(groups: SavedTreeItem[]): TreeNode<TreeDataItem>[] {
    return groups.map((group) => {
      const childGroups = this.store.getChildren(group.id).filter((child) => isGroup(child));

      const node: TreeNode<TreeDataItem> = {
        key: group.id,
        label: group.key,
        data: group,
        selectable: true,
        leaf: childGroups.length === 0,
      };

      if (childGroups.length > 0) {
        node.children = this.buildGroupTree(childGroups);
        node.expanded = true;
      }

      return node;
    });
  }

  private findInNodes(nodes: TreeNode<TreeDataItem>[], treeNodeKey: string): TreeNode<TreeDataItem> | null {
    for (const node of nodes) {
      if (node.key === treeNodeKey) return node;
      if (node.children) {
        const found = this.findInNodes(node.children, treeNodeKey);
        if (found) return found;
      }
    }

    return null;
  }

  private addPendingItemToParent(
    tree: TreeNode<TreeDataItem>[],
    pendingItem: PendingNewItem,
    parentGroupId: string,
  ): void {
    for (const node of tree) {
      if (node.key === parentGroupId && node.children) {
        const newNode = this.createNewItemNode(pendingItem.type, parentGroupId);
        node.children.push(newNode);

        return;
      }

      if (node.children) {
        this.addPendingItemToParent(node.children, pendingItem, parentGroupId);
      }
    }
  }
}
